import { useEffect } from 'react'

import { getCurrencyAddress, percentToBps } from '@cowprotocol/common-utils'
import { Currency, CurrencyAmount } from '@cowprotocol/currency'
import { AtomsAndUnits, CowWidgetEvents, OnTradeParamsPayload } from '@cowprotocol/events'
import { TokenInfo } from '@cowprotocol/types'

import { WIDGET_EVENT_EMITTER } from 'widgetEventEmitter'

import { AmountsToSign, useAmountsToSignFromQuote } from './useAmountsToSignFromQuote'
import { useDerivedTradeState } from './useDerivedTradeState'

import { TradeTypeToUiOrderType } from '../const/common'
import { TradeDerivedState, TradeType } from '../types'

const ZERO_AMOUNT: AtomsAndUnits = {
  atoms: BigInt(0),
  units: '0',
}

export function useNotifyWidgetTrade(): void {
  const state = useDerivedTradeState()
  const amountsToSign = useAmountsToSignFromQuote()

  useEffect(() => {
    if (!state) return

    /**
     * There is no way to select both empty sell and buy currencies in the widget UI.
     * The only way when it is possible is when the widget integrator set the state, but in this case it doesn't make sense to notify them.
     *
     * In practice, the state has both currencies empty only at the beginning, when the widget is not ready to trade.
     * So we skip the notification in this case.
     */
    const stateIsNotReady = !state.inputCurrency && !state.outputCurrency

    if (!state.tradeType || stateIsNotReady) {
      return
    }

    WIDGET_EVENT_EMITTER.emit(
      CowWidgetEvents.ON_CHANGE_TRADE_PARAMS,
      getTradeParamsEventPayload(state.tradeType, state, amountsToSign),
    )
  }, [state, amountsToSign])
}

function currencyAmountToAtomsAndUnits(currency: CurrencyAmount<Currency> | null): AtomsAndUnits | undefined {
  if (!currency) return undefined

  return {
    atoms: BigInt(currency.quotient.toString()),
    units: currency.toExact(),
  }
}

function currencyToTokenInfo(currency: Currency | null): TokenInfo | undefined {
  if (!currency) return undefined

  return {
    address: getCurrencyAddress(currency),
    chainId: currency.chainId,
    name: currency.name || '',
    decimals: currency.decimals,
    symbol: currency.symbol || '',
  }
}

function getTradeParamsEventPayload(
  tradeType: TradeType,
  state: TradeDerivedState,
  amountsToSign: AmountsToSign | null,
): OnTradeParamsPayload {
  return {
    orderType: TradeTypeToUiOrderType[tradeType],
    chainId: state.inputCurrency?.chainId,
    sellToken: currencyToTokenInfo(state.inputCurrency),
    buyToken: currencyToTokenInfo(state.outputCurrency),
    sellTokenAmount: currencyAmountToAtomsAndUnits(state.inputCurrencyAmount),
    buyTokenAmount: currencyAmountToAtomsAndUnits(state.outputCurrencyAmount),
    sellTokenBalance: currencyAmountToAtomsAndUnits(state.inputCurrencyBalance),
    buyTokenBalance: currencyAmountToAtomsAndUnits(state.outputCurrencyBalance),
    sellTokenFiatAmount: state.inputCurrencyFiatAmount?.toExact(),
    buyTokenFiatAmount: state.outputCurrencyFiatAmount?.toExact(),
    maximumSendSellAmount: amountsToSign
      ? currencyAmountToAtomsAndUnits(amountsToSign?.maximumSendSellAmount)
      : ZERO_AMOUNT,
    minimumReceiveBuyAmount: amountsToSign
      ? currencyAmountToAtomsAndUnits(amountsToSign?.minimumReceiveBuyAmount)
      : ZERO_AMOUNT,
    recipient: state.recipient || undefined,
    orderKind: state.orderKind,
    slippageBps: state.slippage ? percentToBps(state.slippage) : undefined,
  }
}
